import type { EchoJourneyNode, EchoflicksSessionJourney } from "../types";

export type StoryboardFirstAuditModel = {
  available: boolean;
  decision: string;
  reason: string | null;
  boardId: string | null;
  revision: number | null;
  requiredBeats: number | null;
  coveredBeats: number | null;
  attempts: number;
  selectedProvider: string | null;
  selectedVariant: string | null;
  finalDisposition: string | null;
  finalIssueCodes: string[];
  configReleaseId: string | null;
  configDigest: string | null;
};

const value = (node: EchoJourneyNode | undefined, key: string): unknown =>
  node?.raw_payload?.[key] ?? node?.decision?.[key];

const text = (candidate: unknown): string | null =>
  typeof candidate === "string" && candidate.length ? candidate : null;

const integer = (candidate: unknown): number | null =>
  typeof candidate === "number" && Number.isInteger(candidate)
    ? candidate
    : null;

const latest = (nodes: EchoJourneyNode[], types: string[]) =>
  [...nodes].reverse().find((node) => types.includes(node.type));

export function buildStoryboardFirstAudit(
  journey?: EchoflicksSessionJourney,
): StoryboardFirstAuditModel {
  const nodes = journey?.nodes ?? [];
  const eligibility = latest(nodes, [
    "storyboard_first.eligibility",
    "storyboard.eligibility.decided",
  ]);
  const board = latest(nodes, [
    "storyboard_first.board.accepted",
    "storyboard.board.accepted",
  ]);
  const selection = latest(nodes, [
    "storyboard_first.shot.selected",
    "storyboard.shot.selected",
  ]);
  const delivery = latest(nodes, [
    "storyboard_first.delivery.acceptance",
    "storyboard.delivery.acceptance",
  ]);
  const attempts = nodes.filter((node) =>
    ["storyboard_first.panel.attempt", "storyboard.panel.attempt"].includes(
      node.type,
    ),
  );
  const issueValue = value(delivery, "issue_codes");
  const finalIssueCodes = Array.isArray(issueValue)
    ? issueValue.filter((item): item is string => typeof item === "string")
    : [];

  return {
    available: Boolean(eligibility || board || selection || delivery),
    decision: text(value(eligibility, "decision")) ?? "unavailable",
    reason: text(value(eligibility, "explanation")),
    boardId: text(value(board, "board_id")),
    revision: integer(value(board, "board_revision")),
    requiredBeats: integer(value(board, "required_beat_count")),
    coveredBeats: integer(value(board, "covered_required_beat_count")),
    attempts: attempts.length,
    selectedProvider: text(value(selection, "selected_provider")),
    selectedVariant: text(value(selection, "selected_variant")),
    finalDisposition: text(value(delivery, "disposition")),
    finalIssueCodes,
    configReleaseId:
      text(value(eligibility, "config_release_id")) ??
      text(value(board, "config_release_id")),
    configDigest:
      text(value(eligibility, "config_digest")) ??
      text(value(board, "config_digest")),
  };
}
